import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Modal, Pagination, Row, Table } from "react-bootstrap";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { CompanyType, companyIntegrations, companyServices, companyStatuses } from "../../models/company";
import { useAppSelector } from "../../store";
import { switchToCustomer } from "../../loaders/customer-override";
import { fetchCompanies, postCompany } from "../../utils/http/companies";

function Companies() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeCompany = useAppSelector((state) => state.company.activeCompany);
  const currentUser = useAppSelector((state) => state.user);
  const canAddCompany = currentUser?.roles?.includes("companies-add") || false;

  const queryClient = useQueryClient();

  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyWebsite, setNewCompanyWebsite] = useState("");

  const { mutate: createCompany, isPending: isCreating, error: createError } = useMutation({
    mutationFn: postCompany,
    onSuccess: async (createdCompany: CompanyType) => {
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      setShowAddModal(false);
      navigate(`/companies/${createdCompany.name}`);
    },
  });

  const handleAddCompany = () => {
    createCompany({
      name: newCompanyName.trim(),
      website_url: newCompanyWebsite.trim() || undefined,
    });
  };

  const [statusFilter, setStatusFilter] = useState<string>("active_onboarding");
  const [serviceFilter, setServiceFilter] = useState<string>("");
  const [integrationFilter, setIntegrationFilter] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");
  const [debouncedNameFilter, setDebouncedNameFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);

  // Debounce name filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(nameFilter);
    }, 500);

    return () => clearTimeout(timer);
  }, [nameFilter]);

  const { data: paginatedData } = useQuery({
    queryKey: ["companies", statusFilter, serviceFilter, integrationFilter, debouncedNameFilter, currentPage, pageSize],
    queryFn: ({ signal }) =>
      fetchCompanies({
        signal,
        status: statusFilter || undefined,
        service: serviceFilter || undefined,
        integration: integrationFilter || undefined,
        name: debouncedNameFilter || undefined,
        page: currentPage,
        size: pageSize,
      }),
    staleTime: 2000,
  });

  const companies = paginatedData?.items || [];
  const totalItems = paginatedData?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleServiceChange = (service: string) => {
    setServiceFilter(service);
    setCurrentPage(1);
  };

  const handleIntegrationChange = (integration: string) => {
    setIntegrationFilter(integration);
    setCurrentPage(1);
  };

  const handleNameChange = (name: string) => {
    setNameFilter(name);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setNameFilter("");
    setStatusFilter("active");
    setServiceFilter("");
    setIntegrationFilter("");
    setCurrentPage(1);
  };

  if (!location.pathname.match(/^\/companies(\/)?$/)) {
    return <Outlet />;
  }

  return (
    <div>
      <div className="mb-4 d-flex align-items-center justify-content-between">
        <h3 className="mb-0">Companies</h3>
        {canAddCompany && (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => {
              setNewCompanyName("");
              setNewCompanyWebsite("");
              setShowAddModal(true);
            }}
          >
            <i className="bi bi-plus me-1"></i>Add
          </Button>
        )}
      </div>
      <hr className="mb-4" />
      <Container className="pb-3">
        <Row className="mb-2">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Company Name</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search by name..."
                value={nameFilter}
                onChange={(e) => handleNameChange(e.target.value)}
                style={{ border: "1px solid #999999" }}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Status</Form.Label>
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
                style={{ border: "1px solid #999999" }}
              >
                <option value="">- All statuses -</option>
                <option value="active_onboarding">Active &amp; Onboarding</option>
                {companyStatuses.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Service</Form.Label>
              <Form.Select
                size="sm"
                value={serviceFilter}
                onChange={(e) => handleServiceChange(e.target.value)}
                style={{ border: "1px solid #999999" }}
              >
                <option value="">- All services -</option>
                {companyServices
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label style={{ fontSize: "0.9em", marginBottom: "0.25rem" }}>Integration</Form.Label>
              <Form.Select
                size="sm"
                value={integrationFilter}
                onChange={(e) => handleIntegrationChange(e.target.value)}
                style={{ border: "1px solid #999999" }}
              >
                <option value="">- All integrations -</option>
                {companyIntegrations
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClearFilters}
              style={{ width: "100%", whiteSpace: "nowrap" }}
            >
              <i className="bi bi-x-circle me-2"></i>
              Clear Filters
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <span className="text-muted">
              Showing {companies.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
              {Math.min(currentPage * pageSize, totalItems)} of {totalItems} companies
            </span>
          </Col>
        </Row>
      </Container>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: "180px" }}></th>
            <th>Name</th>
            <th>Status</th>
            <th>Services</th>
            <th>Integrations</th>
          </tr>
        </thead>
        <tbody>
          {companies &&
            companies.map((c: CompanyType) => {
              // Separate services by status
              const activeServicesData = c.services
                ? Object.entries(c.services)
                    .filter(([_, config]: [string, any]) => config?.status === "active")
                    .map(([name]: [string, any]) => name)
                : [];

              const onboardingServicesData = c.services
                ? Object.entries(c.services)
                    .filter(([_, config]: [string, any]) => config?.status === "onboarding")
                    .map(([name]: [string, any]) => name)
                : [];

              // Separate integrations by status
              const activeIntegrationsData = c.integrations
                ? Object.entries(c.integrations)
                    .filter(([_, config]: [string, any]) => config?.status === "active")
                    .map(([name]: [string, any]) => name)
                : [];

              const onboardingIntegrationsData = c.integrations
                ? Object.entries(c.integrations)
                    .filter(([_, config]: [string, any]) => config?.status === "onboarding")
                    .map(([name]: [string, any]) => name)
                : [];

              return (
                <tr key={c.name}>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    {activeCompany?.name !== c.name && (
                      <Button
                        onClick={() => {
                          // Reuse the shared switch: fetches the full company doc
                          // (the list row is only a partial projection) and applies
                          // the same access check as the `?customer=` override.
                          switchToCustomer(c.name);
                        }}
                        className="me-2"
                      >
                        Select
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        navigate(`/companies/${c.name}`);
                      }}
                    >
                      View
                    </Button>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ flex: 1 }}>
                        <div>{c.info?.name}</div>
                        <div style={{ fontSize: "0.85em", color: "#6c757d" }}>
                          {c.name}
                          {c.legacy_data?.id && ` - ${c.legacy_data.id}`}
                        </div>
                      </div>
                      {c.info?.website_url && (
                        <Link to={c.info.website_url} target="_blank" style={{ fontSize: "1.2em" }}>
                          <i className="bi bi-box-arrow-up-right"></i>
                        </Link>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`badge bg-${
                        c.status === "active" ? "success" : c.status === "inactive" ? "secondary" : "warning"
                      }`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td>
                    {activeServicesData.length === 0 && onboardingServicesData.length === 0 ? (
                      "-"
                    ) : (
                      <div>
                        {activeServicesData.length > 0 && (
                          <div style={{ marginBottom: onboardingServicesData.length > 0 ? "8px" : "0" }}>
                            <strong>active</strong>
                            <div style={{ paddingLeft: "10px" }}>{activeServicesData.join(", ")}</div>
                          </div>
                        )}
                        {onboardingServicesData.length > 0 && (
                          <div>
                            <strong>onboarding</strong>
                            <div style={{ paddingLeft: "10px" }}>{onboardingServicesData.join(", ")}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {activeIntegrationsData.length === 0 && onboardingIntegrationsData.length === 0 ? (
                      "-"
                    ) : (
                      <div>
                        {activeIntegrationsData.length > 0 && (
                          <div style={{ marginBottom: onboardingIntegrationsData.length > 0 ? "8px" : "0" }}>
                            <strong>active</strong>
                            <div style={{ paddingLeft: "10px" }}>{activeIntegrationsData.join(", ")}</div>
                          </div>
                        )}
                        {onboardingIntegrationsData.length > 0 && (
                          <div>
                            <strong>onboarding</strong>
                            <div style={{ paddingLeft: "10px" }}>{onboardingIntegrationsData.join(", ")}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
      <Container className="pb-3">
        <Row>
          <Col className="d-flex justify-content-center">
            {totalPages > 1 && (
              <Pagination>
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />

                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                    return (
                      <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                        {page}
                      </Pagination.Item>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return <Pagination.Ellipsis key={`ellipsis-${page}`} disabled />;
                  }
                  return null;
                }).filter(Boolean)}

                <Pagination.Next
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            )}
          </Col>
          {false && (
            <Col className="text-end">
              <Button
                onClick={() => {
                  navigate(`/companies/new`);
                }}
              >
                Create new company
              </Button>
            </Col>
          )}
        </Row>
      </Container>
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Company name"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              autoFocus
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Website</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://example.com"
              value={newCompanyWebsite}
              onChange={(e) => setNewCompanyWebsite(e.target.value)}
            />
          </Form.Group>
          {createError && (
            <div className="text-danger mt-3" style={{ fontSize: "0.9em" }}>
              {(createError as any)?.message || "An error occurred while creating the company."}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddCompany} disabled={isCreating || !newCompanyName.trim()}>
            {isCreating ? "Creating..." : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Companies;
